import { useConsentManager } from '@c15t/react';
import { useState } from 'react';

import { CATEGORIES, type Category } from './categories.js';

import { t } from '../../translations.js';
import { Button } from '../../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog.js';
import { cn } from '../../ui/utils.js';

/**
 * Preferences dialog — replaces c15t's `<ConsentDialog>` with a Radix-backed
 * shadcn `<Dialog>`. The reasons we ship our own:
 *
 *   - Backdrop click + ESC dismissal come for free from Radix (c15t's
 *     `<dialog>`-based variant only gets the latter).
 *   - Visual + a11y consistency with every other dialog in the SPA (project
 *     create, etc.) — same `<Dialog>` primitives.
 *   - Full control over the toggle UX — we use a local pending-state model
 *     so cancelling the dialog reverts any unsaved toggles.
 *
 * c15t still owns the underlying store: `consents` (saved), `saveConsents`
 * persists, and we wire `setSelectedConsent` per-category on save so any
 * downstream c15t-based integration observes the change through the
 * canonical flow.
 */
export function NomaConsentDialog() {
  const { activeUI, setActiveUI, consentInfo } = useConsentManager();
  const open = activeUI === 'dialog';
  // Dismiss returns to the banner only when no consent has been recorded yet.
  // Once `consentInfo` is non-null (any explicit accept/reject/save), the user
  // has made a choice — closing the dialog goes to the normal "nothing
  // showing" state. This matters when the dialog is reopened post-consent (from
  // the account settings cookie controls): Esc / backdrop should just close,
  // not re-summon a banner the user already dismissed.
  const dismissTarget = consentInfo === null ? 'banner' : 'none';
  return (
    <Dialog
      open={open}
      // A controlled Dialog without a DialogTrigger only ever emits a
      // dismissal (Escape / overlay click) — route every change event to the
      // c15t UI state.
      onOpenChange={() => setActiveUI(dismissTarget)}
    >
      {/*
       * Mount the body only while open so its `useState(consents)` initialises
       * fresh from the saved snapshot every time the dialog opens. Avoids the
       * usual "mirror props into state via useEffect" anti-pattern (and the
       * `react-hooks/set-state-in-effect` lint that catches it).
       */}
      {open ? <NomaConsentDialogBody dismissTarget={dismissTarget} /> : null}
    </Dialog>
  );
}

function NomaConsentDialogBody({ dismissTarget }: { dismissTarget: 'banner' | 'none' }) {
  const { setActiveUI, consents, setSelectedConsent, saveConsents } = useConsentManager();
  const [pending, setPending] = useState<Record<Category, boolean>>(() =>
    initialPending(consents),
  );

  const onSave = () => {
    for (const category of CATEGORIES) {
      if (category === 'necessary') continue;
      setSelectedConsent(category, pending[category]);
    }
    void saveConsents('custom');
  };

  return (
    <DialogContent data-testid="consent-dialog">
      <DialogHeader>
        <DialogTitle>{t.consent.dialog.title}</DialogTitle>
        <DialogDescription>{t.consent.dialog.description}</DialogDescription>
      </DialogHeader>

      <ul className="my-4 divide-y divide-border">
        {CATEGORIES.map((category) => (
          <CategoryRow
            key={category}
            category={category}
            checked={pending[category]}
            disabled={category === 'necessary'}
            onChange={(value) => setPending((prev) => ({ ...prev, [category]: value }))}
          />
        ))}
      </ul>

      <div className="flex flex-col-reverse items-stretch justify-between gap-2 sm:flex-row sm:items-center">
        <a
          href="/cookies"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          data-testid="consent-dialog-statement-link"
        >
          {t.consent.dialog.statementLinkLabel}
        </a>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveUI(dismissTarget)}
            data-testid="consent-dialog-cancel-btn"
          >
            {t.consent.dialog.cancel}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            data-testid="consent-dialog-save-btn"
          >
            {t.consent.dialog.save}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

function CategoryRow({
  category,
  checked,
  disabled,
  onChange,
}: {
  category: Category;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  const meta = t.consent.categories[category];
  const switchId = `consent-toggle-${category}`;
  return (
    <li
      className="flex items-start justify-between gap-4 py-3"
      data-testid={`consent-dialog-row-${category}`}
    >
      <div className="flex-1">
        <label htmlFor={switchId} className="block text-sm font-medium">
          {meta.title}
        </label>
        <p className="mt-1 text-xs text-muted-foreground">{meta.description}</p>
      </div>
      <button
        type="button"
        role="switch"
        id={switchId}
        aria-checked={checked}
        aria-label={meta.title}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          checked ? 'bg-primary' : 'bg-input',
          disabled && 'cursor-not-allowed opacity-60',
        )}
        data-testid={`consent-dialog-switch-${category}`}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </li>
  );
}

function initialPending(
  consents: Record<string, boolean> | undefined,
): Record<Category, boolean> {
  return {
    necessary: true,
    measurement: Boolean(consents?.['measurement']),
    marketing: Boolean(consents?.['marketing']),
  };
}
